import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

const GALLERY_FADE_MS = 350;

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    this.#activeMediaId = this.dataset.activeMediaId || '';

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    if (this.hasAttribute('data-limited-gallery') && this.#activeMediaId) {
      queueMicrotask(() => {
        this.#syncLimitedControls();
        this.#focusMedia(this.#activeMediaId, false);
      });
    }
  }

  #controller = new AbortController();

  /** @type {string} */
  #activeMediaId = '';

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    if (this.hasAttribute('data-limited-gallery')) {
      event.promise
        .then(({ detail }) => {
          const mediaId = detail?.resource?.featured_media?.id ?? null;
          this.applyLimitedVariantMedia(mediaId);
        })
        .catch((error) => {
          if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
        });
      return;
    }

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /** @type {number} */
  #mediaApplyToken = 0;

  /**
   * Show default media (first N) plus the selected variant featured media when needed.
   * Does not re-render the gallery or reveal other hidden media.
   *
   * @param {string|number|null|undefined} mediaId
   */
  async applyLimitedVariantMedia(mediaId) {
    const token = ++this.#mediaApplyToken;
    const nextId = mediaId != null && mediaId !== '' ? String(mediaId) : '';
    const items = this.#limitedItems();

    if (!items.length) return;

    const hasTarget = nextId !== '' && items.some((item) => this.#itemMediaId(item) === nextId);
    /** @type {string} */
    let resolvedId = '';

    if (hasTarget) {
      resolvedId = nextId;
    } else {
      const previousIsDefault = items.some(
        (item) =>
          item.dataset.galleryVisibility === 'default' && this.#itemMediaId(item) === this.#activeMediaId
      );
      resolvedId = previousIsDefault ? this.#activeMediaId : this.#firstDefaultMediaId(items);
    }

    const revealIds = new Set(
      items.filter((item) => item.dataset.galleryVisibility === 'default').map((item) => this.#itemMediaId(item))
    );
    if (hasTarget) revealIds.add(resolvedId);

    const previouslyActive = this.#activeMediaId;
    /** @type {HTMLElement[]} */
    const toReveal = [];

    for (const item of items) {
      const id = this.#itemMediaId(item);
      const shouldShow = revealIds.has(id);
      item.removeAttribute('reveal');

      if (shouldShow) {
        if (item.hasAttribute('hidden')) toReveal.push(item);
      } else if (item.dataset.galleryVisibility === 'variant-only') {
        // Instantly drop the previous SKU-only media so it cannot linger during async work.
        item.setAttribute('hidden', '');
        item.style.removeProperty('opacity');
        item.style.removeProperty('transition');
        item.classList.remove('is-gallery-fading');
      }
    }

    if (toReveal.length) {
      await Promise.all(toReveal.map((item) => this.#fadeReveal(item)));
    }

    if (token !== this.#mediaApplyToken) return;

    this.#activeMediaId = resolvedId;
    if (resolvedId) this.dataset.activeMediaId = resolvedId;
    else delete this.dataset.activeMediaId;

    this.#syncLimitedControls();
    await this.#focusMedia(resolvedId, previouslyActive !== resolvedId);
  }

  /**
   * @returns {HTMLElement[]}
   */
  #limitedItems() {
    return /** @type {HTMLElement[]} */ ([
      ...this.querySelectorAll('[data-gallery-visibility][data-media-id]'),
    ]);
  }

  /**
   * @param {HTMLElement} item
   */
  #itemMediaId(item) {
    return String(item.dataset.mediaId || '');
  }

  /**
   * @param {HTMLElement[]} items
   */
  #firstDefaultMediaId(items) {
    const first = items.find((item) => item.dataset.galleryVisibility === 'default');
    return first ? this.#itemMediaId(first) : '';
  }

  /**
   * @param {HTMLElement} item
   */
  async #fadeReveal(item) {
    item.removeAttribute('hidden');
    item.removeAttribute('reveal');
    item.style.opacity = '0';
    item.classList.add('is-gallery-fading');

    await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));

    item.style.transition = `opacity ${GALLERY_FADE_MS}ms ease`;
    item.style.opacity = '1';

    await new Promise((resolve) => setTimeout(resolve, GALLERY_FADE_MS));

    item.classList.remove('is-gallery-fading');
    item.style.removeProperty('opacity');
    item.style.removeProperty('transition');
  }

  #syncLimitedControls() {
    const visibleSlides = [
      ...this.querySelectorAll('slideshow-slide[data-gallery-visibility]:not([hidden])'),
    ];
    const visibleCount = Math.max(visibleSlides.length, 1);
    const visibleIds = new Set(
      visibleSlides.map((slide) => String(slide.getAttribute('slide-id') || slide.dataset.mediaId || ''))
    );

    this.querySelectorAll('[data-gallery-total]').forEach((el) => {
      el.textContent = String(visibleCount);
    });

    this.querySelectorAll('[data-gallery-visibility][data-media-id]').forEach((el) => {
      if (!(el instanceof HTMLElement)) return;
      if (el.matches('slideshow-slide, .media-gallery__grid > *, .dialog-zoomed-gallery > *')) return;

      const id = String(el.dataset.mediaId || '');
      if (el.dataset.galleryVisibility === 'default') {
        el.removeAttribute('hidden');
        return;
      }
      if (visibleIds.has(id)) el.removeAttribute('hidden');
      else el.setAttribute('hidden', '');
    });
  }

  /**
   * @param {string} mediaId
   * @param {boolean} animate
   */
  async #focusMedia(mediaId, animate = true) {
    if (!mediaId) return;

    const slideshow = this.slideshow;
    if (slideshow?.select) {
      try {
        await slideshow.select({ id: mediaId }, undefined, { animate });
      } catch {
        // Slideshow may not be ready on first paint; ignore.
      }
    }

    const gridItem = this.querySelector(
      `.media-gallery__grid > [data-gallery-visibility][data-media-id="${CSS.escape(mediaId)}"]:not([hidden])`
    );
    if (gridItem instanceof HTMLElement) {
      gridItem.scrollIntoView({ behavior: animate ? 'smooth' : 'auto', block: 'nearest' });
    }
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
